import { darken } from "polished";
import styled from "styled-components";
import { ButtonProps } from "./types";

/**
 * Estilo do componente Button
 *
 * Configura estilização dinâmica com base nas props fornecidas.
 */
export const StyledButton = styled.button.withConfig({
  shouldForwardProp: (prop) =>
    ![
      "size",
      "fontWeight",
      "uppercase",
      "color",
      "backgroundColor",
      "rounded",
      "width",
      "height",
    ].includes(prop),
})<ButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme, size }) => {
    switch (size) {
      case "small":
        return `${theme.spacing(1)} ${theme.spacing(2)}`;
      case "large":
        return `${theme.spacing(1.5)} ${theme.spacing(4)}`;
      case "ghost":
        return "0";
      default:
        return `${theme.spacing(1.25)} ${theme.spacing(3)}`;
    }
  }};
  font-size: ${({ theme, size }) => {
    switch (size) {
      case "small":
        return theme.fontSizes.sm;
      case "large":
        return theme.fontSizes.lg;
      case "extra-large":
        return theme.fontSizes.xl;
      case "ghost":
        return theme.fontSizes.md;
      default:
        return theme.fontSizes.md;
    }
  }};
  height: ${({ theme, height, size }) => {
    if (height) {
      return `${height}px`;
    } else {
      return "auto";
    }
  }};

  width: ${({ width }) =>
    typeof width === "number" ? `${width}px` : width || "auto"};

  font-weight: ${({ theme, fontWeight }) =>
    fontWeight ? theme.fontWeights[fontWeight] : theme.fontWeights.normal};
  text-transform: ${({ uppercase }) => (uppercase ? "uppercase" : "none")};
  color: ${({ theme, size, color }) =>
    size === "ghost"
      ? theme.colors.mediumBlue
      : color
        ? theme.colors[color]
        : theme.colors.white};
  background-color: ${({ theme, size, backgroundColor }) =>
    size === "ghost"
      ? "transparent"
      : backgroundColor
        ? theme.colors[backgroundColor]
        : theme.colors.lightBlue};
  border-radius: ${({ theme, rounded }) =>
    rounded ? theme.borderRadius.full : theme.borderRadius.medium};
  border: ${({ theme, size, backgroundColor }) =>
    size === "ghost"
      ? "none"
      : `1px solid ${backgroundColor ? theme.colors[backgroundColor] : theme.colors.lightBlue}`};
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: ${({ theme, size }) => {
      switch (size) {
        case "small":
          return theme.fontSizes.xs;
        case "large":
          return theme.fontSizes.sm;
        case "ghost":
          return theme.fontSizes.sm;
        default:
          return theme.fontSizes.sm;
      }
    }};
    padding: ${({ theme, size }) => {
      switch (size) {
        case "small":
          return `${theme.spacing(0.5)} ${theme.spacing(1.5)}`;
        case "large":
          return `${theme.spacing(1)} ${theme.spacing(3)}`;
        case "ghost":
          return "0";
        default:
          return `${theme.spacing(1)} ${theme.spacing(2.5)}`;
      }
    }};

    border-radius: ${({ theme, rounded }) =>
      rounded ? theme.borderRadius.full : theme.borderRadius.extraLarge};
  }

  &:hover {
    background-color: ${({ theme, backgroundColor, size }) =>
      size === "ghost"
        ? "transparent"
        : backgroundColor
          ? darken(0.05, theme.colors[backgroundColor])
          : darken(0.05, theme.colors.lightBlue)};
  }

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.lightGray};
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

export const IconWrapper = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== "size",
})<{ size: string | undefined }>`
  display: inline-flex;
  align-items: center;
  font-size: ${({ size, theme }) => {
    switch (size) {
      case "small":
        return `${theme.fontSizes.sm}`;
      case "large":
        return `${theme.fontSizes.lg}`;
      case "ghost":
        return `${theme.fontSizes.md}`;
      default:
        return `${theme.fontSizes.md}`;
    }
  }};
`;
